import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../api/axios'
import {
  EnvelopeOpenIcon,
  EyeIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CodeBracketIcon,
  BookmarkSquareIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface EmailTemplate {
  id: string
  template_key: string
  name: string
  description: string | null
  subject: string
  html_body: string
  text_body: string | null
  variables: string[]
  is_enabled: boolean
  updated_by: string | null
  updated_at: string | null
}

interface DefaultMeta {
  template_key: string
  name: string
  description: string
  subject: string
  variables: string[]
}

type Tone = 'professional' | 'friendly' | 'urgent' | 'celebratory'

const TONE_META: Record<Tone, { label: string; hint: string }> = {
  professional: { label: 'Professional', hint: 'Formal and concise' },
  friendly: { label: 'Friendly', hint: 'Warm and personal' },
  urgent: { label: 'Urgent', hint: 'Direct, action-driven' },
  celebratory: { label: 'Celebratory', hint: 'Enthusiastic' },
}

const SAMPLE_VARS: Record<string, string> = {
  firstName: 'Taylor',
  companyName: 'Acme Construction',
  companyCode: 'acme-construction',
  contactName: 'Taylor Jones',
  contactEmail: 'taylor@acme.example',
  contactPhone: '555-123-4567',
  industry: 'Construction',
  employeeCount: '25 employees',
  portalUrl: 'https://admin.example.com',
  superAdminPortalUrl: 'https://admin.cruzitservices.com',
  trialEndsAtFormatted: 'Mon, July 15, 2026',
  trialDays: '90',
  ipAddress: '127.0.0.1',
  companyId: 'cmp_sample',
  qrDataUrl: '',
  iosUrl: 'https://apps.apple.com',
  androidUrl: 'https://play.google.com',
  stackAuthConfigured: 'true',
  provisioned: 'true',
  supportEmail: 'support@cruzitservices.com',
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [defaults, setDefaults] = useState<DefaultMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [draft, setDraft] = useState<EmailTemplate | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewSubject, setPreviewSubject] = useState('')
  const [varOverrides, setVarOverrides] = useState<Record<string, string>>({})
  const [testEmail, setTestEmail] = useState('')
  const [testStatus, setTestStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [testSending, setTestSending] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [splitPct, setSplitPct] = useState<number>(() => {
    const stored = localStorage.getItem('emailTplSplit')
    return stored ? Number(stored) : 50
  })
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')

  const [pendingRecovery, setPendingRecovery] = useState<{ subject: string; html_body: string; savedAt: number } | null>(null)

  // AI assist panel
  const [aiOpen, setAiOpen] = useState(false)
  const [aiNotes, setAiNotes] = useState('')
  const [aiTone, setAiTone] = useState<Tone>('professional')
  const [aiGenerateSubject, setAiGenerateSubject] = useState(true)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiMessage, setAiMessage] = useState<{ ok: boolean; msg: string } | null>(null)

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const splitRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.get('/super-admin/email-templates')
      setTemplates(data.templates || [])
      setDefaults(data.defaults || [])
      if (!selectedKey && data.templates?.length) {
        setSelectedKey(data.templates[0].template_key)
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load email templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  useEffect(() => {
    if (!selectedKey) { setDraft(null); setPendingRecovery(null); return }
    const t = templates.find(t => t.template_key === selectedKey)
    if (t) {
      setDraft({ ...t })
      setDirty(false)
      setSaved(false)
      try {
        const raw = localStorage.getItem(`emailTplDraft:${t.template_key}`)
        if (raw) {
          const parsed = JSON.parse(raw) as { subject: string; html_body: string; savedAt: number }
          if (parsed && (parsed.subject !== t.subject || parsed.html_body !== t.html_body)) {
            setPendingRecovery(parsed)
          } else {
            setPendingRecovery(null)
            localStorage.removeItem(`emailTplDraft:${t.template_key}`)
          }
        } else {
          setPendingRecovery(null)
        }
      } catch {
        setPendingRecovery(null)
      }
    }
  }, [selectedKey, templates])

  useEffect(() => {
    if (!draft || !dirty) return
    const key = `emailTplDraft:${draft.template_key}`
    const t = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({
          subject: draft.subject,
          html_body: draft.html_body,
          savedAt: Date.now(),
        }))
      } catch { /* quota exceeded — ignore */ }
    }, 600)
    return () => clearTimeout(t)
  }, [draft, dirty])

  const restoreDraft = () => {
    if (!pendingRecovery || !draft) return
    setDraft({ ...draft, subject: pendingRecovery.subject, html_body: pendingRecovery.html_body })
    setDirty(true)
    setPendingRecovery(null)
  }

  const discardDraft = () => {
    if (!draft) return
    localStorage.removeItem(`emailTplDraft:${draft.template_key}`)
    setPendingRecovery(null)
  }

  const formatRelative = (ts: number) => {
    const sec = Math.max(1, Math.round((Date.now() - ts) / 1000))
    if (sec < 60) return `${sec}s ago`
    const min = Math.round(sec / 60)
    if (min < 60) return `${min}m ago`
    const hr = Math.round(min / 60)
    if (hr < 24) return `${hr}h ago`
    return `${Math.round(hr / 24)}d ago`
  }

  const varsForDraft = useMemo(() => {
    if (!draft) return []
    const list = draft.variables && draft.variables.length
      ? draft.variables
      : defaults.find(d => d.template_key === draft.template_key)?.variables ?? []
    return list
  }, [draft, defaults])

  const buildVars = () => {
    const vars: Record<string, string> = {}
    for (const k of varsForDraft) {
      vars[k] = varOverrides[k] ?? SAMPLE_VARS[k] ?? `{{${k}}}`
    }
    return vars
  }

  const refreshPreview = async () => {
    if (!draft) return
    try {
      const { data } = await api.post(`/super-admin/email-templates/${draft.template_key}/preview`, {
        variables: buildVars(),
        subject: draft.subject,
        html_body: draft.html_body,
        text_body: draft.text_body,
      })
      setPreviewHtml(data.html || '')
      setPreviewSubject(data.subject || '')
    } catch (e: any) {
      setError(e.response?.data?.message || 'Preview failed')
    }
  }

  useEffect(() => {
    if (!draft) return
    const t = setTimeout(refreshPreview, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.subject, draft?.html_body, draft?.text_body, varOverrides, selectedKey])

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument
      if (doc) {
        doc.open()
        doc.write(previewHtml || '<p style="color:#94a3b8;font-family:sans-serif;padding:24px;">No preview</p>')
        doc.close()
      }
    }
  }, [previewHtml])

  // Drag-to-resize split
  const onDragStart = (e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !splitRef.current) return
      const rect = splitRef.current.getBoundingClientRect()
      const pct = ((e.clientX - rect.left) / rect.width) * 100
      const clamped = Math.max(25, Math.min(75, pct))
      setSplitPct(clamped)
    }
    const onUp = () => {
      if (!dragging.current) return
      dragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      localStorage.setItem('emailTplSplit', String(splitPct))
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [splitPct])

  const onChangeDraft = useCallback((patch: Partial<EmailTemplate>) => {
    setDraft(prev => (prev ? { ...prev, ...patch } : prev))
    setDirty(true)
    setSaved(false)
  }, [])

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setError('')
    try {
      const { data } = await api.put(`/super-admin/email-templates/${draft.template_key}`, {
        name: draft.name,
        description: draft.description,
        subject: draft.subject,
        html_body: draft.html_body,
        text_body: draft.text_body,
        variables: draft.variables,
        is_enabled: draft.is_enabled,
      })
      setTemplates(prev => prev.map(t => t.template_key === data.template_key ? data : t))
      setDraft(data)
      setDirty(false)
      setSaved(true)
      localStorage.removeItem(`emailTplDraft:${data.template_key}`)
      setPendingRecovery(null)
      setTimeout(() => setSaved(false), 2500)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const resetToDefault = async () => {
    if (!draft) return
    if (!confirm(`Reset "${draft.name || draft.template_key}" to the built-in default? Your current subject, HTML, and variables will be overwritten.`)) return
    setResetting(true)
    setError('')
    try {
      const { data } = await api.post(`/super-admin/email-templates/${draft.template_key}/reset`)
      setTemplates(prev => prev.map(t => t.template_key === data.template_key ? data : t))
      setDraft(data)
      setDirty(false)
      setSaved(true)
      localStorage.removeItem(`emailTplDraft:${data.template_key}`)
      setPendingRecovery(null)
      setTimeout(() => setSaved(false), 2500)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to reset template')
    } finally {
      setResetting(false)
    }
  }

  const sendTest = async () => {
    if (!draft || !testEmail) return
    setTestSending(true)
    setTestStatus(null)
    try {
      await api.post(`/super-admin/email-templates/${draft.template_key}/test-send`, {
        to: testEmail,
        variables: buildVars(),
        subject: draft.subject,
        html_body: draft.html_body,
      })
      setTestStatus({ ok: true, msg: `Test sent to ${testEmail}` })
    } catch (e: any) {
      setTestStatus({ ok: false, msg: e.response?.data?.message || 'Test send failed' })
    } finally {
      setTestSending(false)
    }
  }

  const insertVariable = (name: string) => {
    if (!draft) return
    const token = `{{${name}}}`
    const ta = document.getElementById('html-body-editor') as HTMLTextAreaElement | null
    if (ta && ta.selectionStart !== undefined) {
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const next = draft.html_body.slice(0, start) + token + draft.html_body.slice(end)
      onChangeDraft({ html_body: next })
      setTimeout(() => {
        ta.focus()
        ta.setSelectionRange(start + token.length, start + token.length)
      }, 0)
    } else {
      onChangeDraft({ html_body: draft.html_body + token })
    }
  }

  const runAI = async () => {
    if (!draft) return
    setAiBusy(true)
    setAiMessage(null)
    try {
      const { data } = await api.post(`/super-admin/email-templates/${draft.template_key}/ai-generate`, {
        notes: aiNotes,
        tone: aiTone,
        subject: aiGenerateSubject ? '' : draft.subject,
        variables: varsForDraft,
        generateSubject: aiGenerateSubject,
      })
      onChangeDraft({
        html_body: data.html || draft.html_body,
        subject: data.subject || draft.subject,
      })
      setAiMessage({
        ok: true,
        msg: data.source === 'ai'
          ? 'Content generated — review and save.'
          : 'AI is not configured (GROQ_API_KEY missing). A basic template was used instead.',
      })
    } catch (e: any) {
      setAiMessage({ ok: false, msg: e.response?.data?.message || 'Generation failed' })
    } finally {
      setAiBusy(false)
    }
  }

  return (
    <div className="p-6 max-w-[1800px] mx-auto flex flex-col h-[calc(100vh-1rem)]">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <EnvelopeOpenIcon className="w-7 h-7 text-blue-600" />
            Email Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Edit HTML, subject, and variables for transactional emails. Changes apply to future sends immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {draft && (
            <button
              onClick={() => setAiOpen(true)}
              style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
              className="px-4 py-2 text-sm font-semibold rounded-lg hover:brightness-110 flex items-center gap-1.5 shadow-md border border-indigo-700"
            >
              <SparklesIcon className="w-4 h-4" />
              AI Assist
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Template tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-slate-200 overflow-x-auto">
        {templates.map(t => (
          <button
            key={t.template_key}
            onClick={() => setSelectedKey(t.template_key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
              selectedKey === t.template_key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            {t.name}
            {selectedKey === t.template_key && dirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved changes" />
            )}
            {!t.is_enabled && (
              <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">off</span>
            )}
          </button>
        ))}
        {loading && !templates.length && (
          <div className="px-3 py-2 text-xs text-slate-500">Loading templates…</div>
        )}
      </div>

      {pendingRecovery && draft && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <div className="flex-1">
            <div className="font-medium">Unsaved draft from {formatRelative(pendingRecovery.savedAt)}</div>
            <div className="text-xs text-amber-700 mt-0.5">You closed this template before saving. Restore the draft, or discard it to keep the saved version.</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={restoreDraft} className="px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700">Restore</button>
            <button onClick={discardDraft} className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-lg hover:bg-amber-100">Discard</button>
          </div>
        </div>
      )}

      {/* Split editor / preview */}
      {draft ? (
        <div ref={splitRef} className="flex-1 flex min-h-0 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {/* Editor */}
          <div style={{ width: `${splitPct}%` }} className="flex flex-col min-w-0">
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <CodeBracketIcon className="w-4 h-4" /> Editor · <span className="font-mono normal-case text-slate-500">{draft.template_key}</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={draft.is_enabled}
                    onChange={e => onChangeDraft({ is_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <button
                  onClick={resetToDefault}
                  disabled={resetting || saving}
                  title="Overwrite this template with the built-in default"
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1.5"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
                  {resetting ? 'Resetting…' : 'Reset to default'}
                </button>
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5"
                >
                  <BookmarkSquareIcon className="w-3.5 h-3.5" />
                  {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
                </button>
              </div>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Subject</label>
                <input
                  type="text"
                  value={draft.subject}
                  onChange={e => onChangeDraft({ subject: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-col">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">HTML Body</label>
                  <div className="text-[11px] text-slate-400">
                    Use <code className="bg-slate-100 px-1 rounded">{'{{varName}}'}</code>
                  </div>
                </div>
                <textarea
                  id="html-body-editor"
                  value={draft.html_body}
                  onChange={e => onChangeDraft({ html_body: e.target.value })}
                  spellCheck={false}
                  className="mt-1 w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  style={{ minHeight: 260, height: '40vh' }}
                />
              </div>

              {varsForDraft.length > 0 && (
                <div>
                  <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Insert Variable</label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {varsForDraft.map(v => (
                      <button
                        key={v}
                        onClick={() => insertVariable(v)}
                        className="px-2 py-1 text-[11px] font-mono bg-slate-100 hover:bg-blue-100 text-slate-700 border border-slate-200 rounded"
                        title="Click to insert into body"
                      >
                        {'{{'}{v}{'}}'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <details className="group">
                <summary className="cursor-pointer text-[11px] font-semibold text-slate-600 uppercase tracking-wide hover:text-slate-900 flex items-center gap-1">
                  Preview Variables ({varsForDraft.length})
                  <span className="text-slate-400 group-open:hidden">— click to customize</span>
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {varsForDraft.map(v => (
                    <div key={v} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-slate-500 w-32 truncate" title={v}>{v}</span>
                      <input
                        type="text"
                        value={varOverrides[v] ?? SAMPLE_VARS[v] ?? ''}
                        onChange={e => setVarOverrides({ ...varOverrides, [v]: e.target.value })}
                        className="flex-1 px-2 py-1 text-[11px] border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  ))}
                </div>
              </details>

              <div className="pt-3 border-t border-slate-200">
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <PaperAirplaneIcon className="w-3.5 h-3.5" /> Send Test
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={sendTest}
                    disabled={!testEmail || testSending}
                    className="px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                  >
                    {testSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {testStatus && (
                  <div className={`mt-2 text-xs flex items-center gap-1.5 ${testStatus.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                    {testStatus.ok ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
                    {testStatus.msg}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Drag handle */}
          <div
            onMouseDown={onDragStart}
            className="w-1.5 bg-slate-100 hover:bg-blue-300 active:bg-blue-500 cursor-col-resize flex-shrink-0 relative group"
            title="Drag to resize"
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
          </div>

          {/* Preview */}
          <div style={{ width: `${100 - splitPct}%` }} className="flex flex-col min-w-0 bg-slate-50">
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <EyeIcon className="w-4 h-4" /> Preview
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`px-2 py-1 rounded ${previewDevice === 'desktop' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                    title="Desktop"
                  >
                    <ComputerDesktopIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`px-2 py-1 rounded ${previewDevice === 'mobile' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                    title="Mobile"
                  >
                    <DevicePhoneMobileIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  onClick={refreshPreview}
                  className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                >
                  <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>
            </div>
            {previewSubject && (
              <div className="px-4 py-2 border-b border-slate-200 bg-white flex-shrink-0">
                <div className="text-[10px] font-semibold text-slate-500 uppercase">Subject</div>
                <div className="text-sm text-slate-900 font-mono truncate" title={previewSubject}>{previewSubject}</div>
              </div>
            )}
            <div className="flex-1 overflow-auto p-4 flex justify-center items-start">
              <div
                className="bg-white shadow-lg rounded-lg overflow-hidden border border-slate-200 transition-all duration-200"
                style={{
                  width: previewDevice === 'mobile' ? 390 : '100%',
                  maxWidth: previewDevice === 'mobile' ? 390 : 720,
                  height: '100%',
                  minHeight: 400,
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="email preview"
                  className="w-full h-full bg-white"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl">
          Select a template to edit.
        </div>
      )}

      {/* AI assist drawer */}
      {aiOpen && draft && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => !aiBusy && setAiOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-[420px] bg-white z-50 shadow-2xl border-l border-slate-200 flex flex-col">
            <div
              style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
              className="px-5 py-4 border-b border-indigo-700 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-5 h-5" />
                <div>
                  <div className="text-sm font-bold">AI Assist</div>
                  <div className="text-[11px] opacity-80">Generate HTML for {draft.name}</div>
                </div>
              </div>
              <button onClick={() => setAiOpen(false)} disabled={aiBusy} className="p-1 rounded hover:bg-white/10">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Notes / direction</label>
                <textarea
                  value={aiNotes}
                  onChange={e => setAiNotes(e.target.value)}
                  rows={8}
                  placeholder="e.g. Warm welcome. Thank them for starting a trial, direct them to the portal, explain the 90-day window, highlight the mobile QR code. Keep it short."
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Leave empty to let the AI generate a sensible default for this template.
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Tone</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {(Object.keys(TONE_META) as Tone[]).map(k => (
                    <button
                      key={k}
                      onClick={() => setAiTone(k)}
                      className={`px-3 py-2 text-left rounded-lg border transition-all ${
                        aiTone === k
                          ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{TONE_META[k].label}</div>
                      <div className="text-[11px] text-slate-500">{TONE_META[k].hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={aiGenerateSubject}
                  onChange={e => setAiGenerateSubject(e.target.checked)}
                />
                Also generate a new subject line
              </label>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Available variables</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {varsForDraft.map(v => (
                    <code key={v} className="text-[10px] font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-700">
                      {'{{'}{v}{'}}'}
                    </code>
                  ))}
                  {!varsForDraft.length && <span className="text-[11px] text-slate-500">(none)</span>}
                </div>
              </div>

              {aiMessage && (
                <div className={`text-xs p-2 rounded flex items-start gap-1.5 ${aiMessage.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {aiMessage.ok ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <span>{aiMessage.msg}</span>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <button
                onClick={() => setAiOpen(false)}
                disabled={aiBusy}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                Close
              </button>
              <button
                onClick={runAI}
                disabled={aiBusy}
                style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                className="px-4 py-2 text-sm font-semibold rounded-lg hover:brightness-110 disabled:opacity-50 flex items-center gap-1.5 shadow-md border border-indigo-700"
              >
                {aiBusy ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
